import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Stack } from '@pflegenavi/web-components';
import { Typography, Card, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { Timeline, timelineItemClasses } from '@mui/lab';
import { FMPendingChargeItem } from './FMPendingChargeItem';
import type { PendingCharge } from '@pflegenavi/frontend/api-nursing-home';
import { isFailedCharge } from '@pflegenavi/shared/frontend';

type SimplifiedPendingCharge = Pick<
  PendingCharge,
  | 'charge_date'
  | 'payment_initiation'
  | 'amount'
  | 'pending_charge_status'
  | 'source_of_mandate'
  | 'family_member_first_name'
  | 'family_member_last_name'
>;

interface FMPendingChargesCardProps {
  pendingCharges: SimplifiedPendingCharge[];
  currentBalance: number;
  nursingHomeName: string;
}

export const FMPendingChargesCard: FC<FMPendingChargesCardProps> = ({
  pendingCharges,
  currentBalance,
  nursingHomeName,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const sortedCharges = useMemo(() => {
    return [...pendingCharges].sort(
      (a, b) =>
        new Date(a.charge_date).getTime() - new Date(b.charge_date).getTime()
    );
  }, [pendingCharges]);

  const totalPendingAmount = sortedCharges.reduce(
    (sum, charge) => sum + charge.amount,
    0
  );
  const newBalance = currentBalance + totalPendingAmount;

  if (sortedCharges.length === 0) {
    return null;
  }

  const hasFailedCharges = sortedCharges.some(isFailedCharge);

  if (sortedCharges.length === 1) {
    return (
      <Card
        sx={{
          py: 2,
          px: 3,
          mb: 3,
        }}
      >
        {hasFailedCharges && (
          <Alert severity="error" sx={{ mb: 2, alignItems: 'center' }}>
            {t('mobile.family-member.failed-payment-contact-nursing-home')}
          </Alert>
        )}
        <FMPendingChargeItem
          charge={sortedCharges[0]}
          currentBalance={currentBalance}
          newBalance={newBalance}
          nursingHomeName={nursingHomeName}
          isLast={true}
        />
      </Card>
    );
  }

  return (
    <Card
      sx={{
        py: 2,
        px: 3,
        mb: 3,
        flexWrap: 'wrap',
      }}
    >
      {hasFailedCharges && (
        <Alert severity="error" sx={{ mb: 2, alignItems: 'center' }}>
          {t('mobile.family-member.failed-payment-contact-nursing-home')}
        </Alert>
      )}
      <Stack direction="row" sx={{ width: '100%', flexWrap: 'wrap' }}>
        <Stack>
          <Timeline
            sx={{
              minWidth: 400,

              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {sortedCharges.map((charge, index) => (
              <FMPendingChargeItem
                key={index}
                charge={charge}
                currentBalance={currentBalance}
                newBalance={newBalance}
                nursingHomeName={nursingHomeName}
                isLast={index === sortedCharges.length - 1}
                showInTimeline={true}
              />
            ))}
          </Timeline>
        </Stack>
        <Stack
          direction="row"
          sx={{
            ml: 'auto',
            mr: {
              xs: 'auto',
              md: 0,
            },
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              mb: 2,
              mx: 'auto',
              minWidth: 120,
              mt: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {t('payments.total-pending-amount')}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
              }}
            >
              {fCurrencyCents(totalPendingAmount)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mt: 1,
              }}
            >
              {t('mobile.family-member.new-account-balance')}:{' '}
              {fCurrencyCents(newBalance)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
